import Swal from "sweetalert2";
import {
  API_ERROR,
  FETCH_PLAYER_ASSETS,
  FETCH_PLAYER_ASSETS_START,
  FETCH_PLAYER_ASSETS_FAILED,
} from "../constants/ActionTypes";
import { apiV2, errorHandler } from "./index";
import { UNCONFIRMED_MESSAGE } from "../constants/playerAssetTypes";

//= ===============================
// Player Asset actions (avatars / backgrounds / app music)
//= ===============================

// Each fetch carries an id. The reducer keeps the newest id per type and drops
// anything older, so a slow response cannot overwrite a newer list (for example
// the refresh after a delete, which would otherwise restore the deleted row).
let requestSequence = 0;

export function fetchPlayerAssets(type) {
  return function (dispatch) {
    const requestId = ++requestSequence;
    dispatch({ type: FETCH_PLAYER_ASSETS_START, payload: { type, requestId } });

    return apiV2
      .get(`/player-assets`, { params: { type } })
      .then(response => {
        dispatch({
          type: FETCH_PLAYER_ASSETS,
          payload: { type, requestId, assets: response.data.objects || [] },
        });
        return response;
      })
      .catch(error => {
        // A request that never reached the server has no error.response, and
        // errorHandler shows nothing for those, so the screen must say so itself.
        dispatch({
          type: FETCH_PLAYER_ASSETS_FAILED,
          payload: { type, requestId, error: error?.response?.data?.message || "Could not load the list." },
        });
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

export function fetchPlayerAsset(id) {
  return function (dispatch) {
    return apiV2
      .get(`/player-assets/${id}`)
      .then(response => response.data)
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

export function savePlayerAsset(data, id) {
  return function (dispatch) {
    const request = id ? apiV2.put(`/player-assets/${id}`, data) : apiV2.post("/player-assets", data);

    return request
      .then(response => response.data)
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

export function deletePlayerAsset(id) {
  return function (dispatch) {
    return apiV2.delete(`/player-assets/${id}`).catch(error => {
      errorHandler(dispatch, error.response, API_ERROR);
      throw error;
    });
  };
}

// Persist a drag-to-reorder drop. `ids` must be the COMPLETE, ordered set of
// that type's asset ids (active and inactive — the list shows both); the
// server rejects a partial/stale list with a 400 if another admin changed
// the set in the meantime.
//
// On success the redux list for `type` is updated in place to the new order,
// using a fresh requestId from the same sequence `fetchPlayerAssets` uses.
// That makes this update "current" for the reducer's staleness check, so a
// slower fetch that was already in flight before the drop cannot land after
// it and undo the reorder.
export function reorderPlayerAssets(type, ids) {
  return function (dispatch, getState) {
    // If any fetch for this type starts while the save is in flight (a delete's
    // or an edit's refresh), the store may already hold newer data than the
    // list captured here; writing it back would resurrect a deleted row. In
    // that case re-fetch instead of writing.
    const requestIdAtStart = getState().player_assets.requestIdByType[type];

    return apiV2
      .put("/player-assets/sort-order", { type, ids })
      .then(() => {
        if (getState().player_assets.requestIdByType[type] !== requestIdAtStart) {
          dispatch(fetchPlayerAssets(type)).catch(() => {});
          return;
        }

        const requestId = ++requestSequence;
        const byId = new Map((getState().player_assets.byType[type] || []).map(asset => [asset.id, asset]));
        const assets = ids.map(id => byId.get(id)).filter(Boolean);

        dispatch({ type: FETCH_PLAYER_ASSETS_START, payload: { type, requestId } });
        dispatch({ type: FETCH_PLAYER_ASSETS, payload: { type, requestId, assets } });

        // Subtle confirmation — fires on each drop without interrupting.
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Order saved",
          timer: 1200,
          showConfirmButton: false,
        });
      })
      .catch(error => {
        // The persisted order may not match what the admin just saw (a
        // partial/stale write was rejected, or the request never landed), so
        // re-fetch and let the table snap back to what the server has.
        dispatch(fetchPlayerAssets(type)).catch(() => {});

        if (error?.response) {
          errorHandler(dispatch, error.response, API_ERROR);
        } else {
          // A request that never reached the server has no error.response,
          // and errorHandler shows nothing for those.
          Swal.fire({ icon: "error", title: "Reorder not confirmed", text: UNCONFIRMED_MESSAGE });
        }
        throw error;
      });
  };
}
